import React from "react";
import { theme, Badge } from "antd";
import {
  DashboardOutlined,
  ContainerOutlined,
  BellOutlined,
  UserOutlined,
  EllipsisOutlined,
  FileProtectOutlined,
  EuroCircleOutlined,
} from "@ant-design/icons";
import { useTranslate, useGetIdentity, useList } from "@refinedev/core";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useAdmin,
  useCustomer,
  useSuperAdmin,
  useTuner,
} from "../../hooks/auth";

type Notification = {
  id: number;
  isRead: boolean;
};

interface BottomTabBarProps {
  onMoreOpen: () => void;
}

interface TabItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  matchPaths: string[];
  onClick: () => void;
}

export const BottomTabBar: React.FC<BottomTabBarProps> = ({ onMoreOpen }) => {
  const { token } = theme.useToken();
  const translate = useTranslate();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = useAdmin();
  const isCustomer = useCustomer();
  const isSuperAdmin = useSuperAdmin();
  const isTuner = useTuner();
  const { data: user } = useGetIdentity();
  const accountId = (user as any)?.account?.id;
  const billingMode = (user as any)?.account?.billingMode;

  const { data: notificationsData } = useList<Notification>({
    resource: "notifications",
    pagination: { pageSize: 20 },
    filters: [{ field: "channel", operator: "in", value: ["in_app", "both"] }],
  });
  const unreadCount =
    notificationsData?.data?.filter((n) => !n.isRead).length || 0;

  const pathname = location.pathname;

  const isActive = (matchPaths: string[]) =>
    matchPaths.some((p) => pathname === p || pathname.startsWith(p + "/"));

  let tabs: TabItem[] = [];

  if (isAdmin || isSuperAdmin) {
    tabs = [
      {
        key: "dashboard",
        label: translate("dashboard.title", "Dashboard"),
        icon: <DashboardOutlined style={{ fontSize: 22 }} />,
        matchPaths: ["/"],
        onClick: () => navigate("/"),
      },
      {
        key: "orders",
        label: translate("orders.orders", "Orders"),
        icon: <ContainerOutlined style={{ fontSize: 22 }} />,
        matchPaths: ["/orders", "/sav"],
        onClick: () => navigate("/orders"),
      },
      {
        key: "notifications",
        label: translate("notifications.notifications", "Notifications"),
        icon: (
          <Badge count={unreadCount} size="small" offset={[4, -2]}>
            <BellOutlined style={{ fontSize: 22 }} />
          </Badge>
        ),
        matchPaths: ["/notifications"],
        onClick: () => navigate("/notifications"),
      },
      {
        key: "more",
        label: translate("navigation.more", "More"),
        icon: <EllipsisOutlined style={{ fontSize: 22 }} />,
        matchPaths: [],
        onClick: onMoreOpen,
      },
    ];
  } else if (isCustomer) {
    const financialTab =
      billingMode === "postpaid"
        ? {
            key: "invoices",
            label: translate("invoices.invoices", "Invoices"),
            icon: <FileProtectOutlined style={{ fontSize: 22 }} />,
            matchPaths: [`/accounts/${accountId}/invoices`],
            onClick: () =>
              navigate(`/accounts/${accountId}/invoices/list`),
          }
        : {
            key: "transactions",
            label: translate("transactions.transactions", "Transactions"),
            icon: <EuroCircleOutlined style={{ fontSize: 22 }} />,
            matchPaths: [`/accounts/${accountId}/transactions`],
            onClick: () =>
              navigate(`/accounts/${accountId}/transactions/list`),
          };

    tabs = [
      {
        key: "dashboard",
        label: translate("dashboard.title", "Dashboard"),
        icon: <DashboardOutlined style={{ fontSize: 22 }} />,
        matchPaths: ["/account-home"],
        onClick: () => navigate("/account-home"),
      },
      {
        key: "orders",
        label: translate("orders.orders", "Orders"),
        icon: <ContainerOutlined style={{ fontSize: 22 }} />,
        matchPaths: [`/accounts/${accountId}/orders`],
        onClick: () => navigate(`/accounts/${accountId}/orders/list`),
      },
      financialTab,
      {
        key: "profile",
        label: translate("profile.profile", "Profile"),
        icon: <UserOutlined style={{ fontSize: 22 }} />,
        matchPaths: ["/settings"],
        onClick: () => navigate("/settings"),
      },
    ];
  } else if (isTuner) {
    tabs = [
      {
        key: "orders",
        label: translate("orders.orders", "Orders"),
        icon: <ContainerOutlined style={{ fontSize: 22 }} />,
        matchPaths: ["/orders"],
        onClick: () => navigate("/orders"),
      },
      {
        key: "more",
        label: translate("navigation.more", "More"),
        icon: <EllipsisOutlined style={{ fontSize: 22 }} />,
        matchPaths: [],
        onClick: onMoreOpen,
      },
    ];
  }

  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        height: 60,
        backgroundColor: token.colorBgElevated,
        borderTop: `1px solid ${token.colorBorderSecondary}`,
        display: "flex",
        alignItems: "stretch",
        zIndex: 1000,
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {tabs.map((tab) => {
        const active = isActive(tab.matchPaths);
        return (
          <button
            key={tab.key}
            onClick={tab.onClick}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              border: "none",
              background: "none",
              cursor: "pointer",
              color: active ? token.colorPrimary : token.colorTextSecondary,
              padding: "6px 0",
              fontSize: 10,
              fontWeight: active ? 600 : 400,
              transition: "color 0.2s",
            }}
          >
            {tab.icon}
            <span style={{ fontSize: 10 }}>{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
};
